import { Send } from 'lucide-react';
import { type FormEvent, type KeyboardEvent, type ReactNode, useId, useState } from 'react';
import { errorMessage } from '../../../lib/api';
import { cn } from '../../../lib/cn';
import { formatDateTimeShort } from '../../../lib/format';
import { FormMessage } from '../../../ui/Blocks';
import { Button } from '../../../ui/Button';
import { Textarea } from '../../../ui/Input';

/**
 * Rozmowa przy zgłoszeniu (portal klienta i karta zadania zespołu — „Rozmowa z klientem”, DECISIONS 2026-10-17 [P]
 * „Model portalu klienta” nr 5): dymki po dwóch stronach. `side` mówi, po której stronie stoi wiadomość z punktu
 * widzenia czytającego (`own` — prawa, z lekkim tłem akcentu; `other` — lewa, szare tło). Imię autora nad dymkiem
 * (gdy jest), czas pod nim. Tekst bez formatowania (łamanie linii zostaje).
 */
export interface ConversationItem {
  id: string;
  side: 'own' | 'other';
  /** imię autora; null = bez podpisu (np. własna wiadomość) */
  authorName: string | null;
  /** dopisek przy imieniu, np. „klient” albo „zespół” */
  authorNote?: string;
  text: string;
  createdAt: Date;
}

export function ConversationList({ items, className }: { items: ConversationItem[]; className?: string }) {
  return (
    <ol className={cn('flex flex-col gap-4', className)} aria-label="Wiadomości">
      {items.map((m) => (
        <li key={m.id} className={cn('flex flex-col', m.side === 'own' ? 'items-end' : 'items-start')}>
          {m.authorName && (
            <span className="mb-1 px-1 text-caption font-medium text-fg-secondary">
              {m.authorName}
              {m.authorNote && <span className="font-normal text-fg-muted"> · {m.authorNote}</span>}
            </span>
          )}
          <p
            className={cn(
              'max-w-[85%] rounded-2xl px-4 py-2.5 text-body-s whitespace-pre-wrap break-words text-fg md:max-w-[75%]',
              m.side === 'own' ? 'rounded-br-md bg-accent-subtle' : 'rounded-bl-md bg-surface',
            )}
          >
            {m.text}
          </p>
          <time dateTime={m.createdAt.toISOString()} className="mt-1 px-1 text-micro text-fg-muted">
            {formatDateTimeShort(m.createdAt)}
          </time>
        </li>
      ))}
    </ol>
  );
}

/**
 * Pole nowej wiadomości: Textarea i „Wyślij” (Ctrl/⌘ + Enter też wysyła). `onSend` zwraca Promise — po sukcesie
 * pole się czyści, przy błędzie tekst zostaje, a komunikat pokazuje się pod polem.
 */
export function MessageComposer({
  onSend,
  sending,
  label,
  placeholder,
  hint,
  maxLength,
  sendLabel = 'Wyślij',
}: {
  onSend: (text: string) => Promise<unknown>;
  sending: boolean;
  /** etykieta pola (dla czytnika ekranu i nad polem) */
  label: string;
  placeholder?: string;
  /** zdanie pod polem, np. „Klient dostanie mail z linkiem do zgłoszenia.” */
  hint?: ReactNode;
  maxLength: number;
  sendLabel?: string;
}) {
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const id = useId();
  const hintId = `${id}-hint`;

  const submit = async (event?: FormEvent) => {
    event?.preventDefault();
    const value = text.trim();
    if (!value) {
      setError('Wpisz treść wiadomości');
      return;
    }
    setError(null);
    try {
      await onSend(value);
      setText('');
    } catch (err) {
      setError(errorMessage(err, 'Nie udało się wysłać. Spróbuj ponownie.'));
    }
  };

  const onKeyDown = (event: KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === 'Enter' && (event.ctrlKey || event.metaKey)) {
      event.preventDefault();
      void submit();
    }
  };

  return (
    <form onSubmit={submit} noValidate className="flex flex-col gap-2">
      <label htmlFor={id} className="sr-only">
        {label}
      </label>
      <Textarea
        id={id}
        rows={3}
        value={text}
        maxLength={maxLength}
        placeholder={placeholder}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={onKeyDown}
        aria-invalid={error ? true : undefined}
        aria-describedby={hint ? hintId : undefined}
      />
      {error && <FormMessage>{error}</FormMessage>}
      <div className="flex flex-col-reverse gap-2 md:flex-row md:items-center md:justify-between">
        {hint ? (
          <p id={hintId} className="text-caption text-fg-muted">
            {hint}
          </p>
        ) : (
          <span />
        )}
        <Button type="submit" variant="primary" icon={Send} loading={sending} className="max-md:h-11 max-md:w-full">
          {sendLabel}
        </Button>
      </div>
    </form>
  );
}
